// The data a Setup page's rules need (the settings, the departments, the staff), and Find a setting's
// way to a rule: no stylesheets, so Riverstone's rows (RuleSection.tsx) and Blueprint's on the
// SettingsPage pattern (SetupRules.tsx) share them.
import { useCallback, useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import type { SettingRow } from './settings-model.ts';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export interface Unit {
  id: string;
  name: string;
}
export interface StaffName {
  id: string;
  displayName: string;
}

export interface RuleData {
  rows: SettingRow[] | null;
  units: Unit[];
  staff: StaffName[];
  error: string | null;
  load: () => Promise<void>;
}

/** The settings, the departments and the staff a page's rules need, loaded once for the page. */
export function useRuleData(on = true): RuleData {
  const [rows, setRows] = useState<SettingRow[] | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [staff, setStaff] = useState<StaffName[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      const r = await client.call<{ settings: SettingRow[] }>('settings.list', {});
      setRows(r.settings);
      setError(null);
    } catch (e) {
      setError(message(e));
    }
  }, []);
  useEffect(() => {
    if (!on) return;
    void load();
    client
      .call<{ businessUnits: Unit[] }>('businessUnits.list', {})
      .then((r) => setUnits(r.businessUnits))
      .catch(() => undefined);
    client
      .call<{ staff: StaffName[] }>('staff.list', {})
      .then((r) => setStaff(r.staff))
      .catch(() => undefined);
  }, [load, on]);
  return { rows, units, staff, error, load };
}

// Find a setting (Company details): the row it opens is marked a moment once its page draws it
let toFind: string | null = null;
const FIND_EVENT = 'flowology:find-setting';

/** Open a setting's row: its page draws it, scrolls to it and marks it (or it already is drawn). */
export function findSetting(key: string) {
  toFind = key;
  window.dispatchEvent(new CustomEvent(FIND_EVENT, { detail: key }));
}

/** Whether a setting's row is the one just found (for a moment), scrolled to once it is drawn. */
export function useFoundSetting(key: string, element: () => HTMLElement | null): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const land = () => {
      if (toFind !== key) return;
      toFind = null;
      element()?.scrollIntoView({ block: 'center' });
      setOn(true);
      window.setTimeout(() => setOn(false), 2500);
    };
    land();
    window.addEventListener(FIND_EVENT, land);
    return () => window.removeEventListener(FIND_EVENT, land);
  }, [key]);
  return on;
}
